import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Spinner,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TableCell,
  TableBody,
  TextInput,
} from "flowbite-react";

import AuctionRuleTemplateFormModal from "../components/auctionRuleTemplates/AuctionRuleTemplateFormModal";
import {
  auctionRuleTemplatesActions,
  auctionRuleTemplatesThunks,
} from "../app/store"; // ajuste o path conforme seu projeto
import AuctionRuleTemplateReadModal from "../components/auctionRuleTemplates/AuctionRuleTemplateReadModal";

function truncate(text, max = 120) {
  if (!text) return "";
  const t = String(text);
  return t.length > max ? `${t.slice(0, max)}...` : t;
}

export default function AuctionRuleTemplatesPage() {
  const dispatch = useDispatch();

  const { items, count, status, error } = useSelector(
    (s) => s.auctionRuleTemplates,
  );

  // Se o backend exigir created_by_admin_id no payload, podemos pegar do auth
  const authUser = useSelector(
    (s) => s.auth?.user || s.auth?.currentUser || null,
  );

  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readOpen, setReadOpen] = useState(false);
  const [reading, setReading] = useState(null);

  const limit = useMemo(() => 20, []);
  const offset = 0;

  function openRead(row) {
    setReading(row);
    setReadOpen(true);
  }

  function closeRead() {
    setReadOpen(false);
    setReading(null);
  }

  useEffect(() => {
    dispatch(auctionRuleTemplatesThunks.list({ q: "", limit, offset }));
  }, [dispatch, limit]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    dispatch(auctionRuleTemplatesActions.clearError());
  }

  function buildListParams() {
    return { q: q || "", limit, offset };
  }

  function onSearch() {
    dispatch(auctionRuleTemplatesActions.clearError());
    dispatch(auctionRuleTemplatesThunks.list(buildListParams()));
  }

  async function onSubmit(payload) {
    // Se o backend exigir created_by_admin_id, injeta aqui quando existir
    const maybeAdminId = authUser?.id || authUser?.user?.id;

    const finalPayload =
      payload?.created_by_admin_id || !maybeAdminId
        ? payload
        : { ...payload, created_by_admin_id: maybeAdminId };

    if (editing?.id) {
      await dispatch(
        auctionRuleTemplatesThunks.updateOne({
          id: editing.id,
          payload: finalPayload,
        }),
      );
      dispatch(auctionRuleTemplatesThunks.list(buildListParams()));
      closeModal();
      return;
    }

    await dispatch(auctionRuleTemplatesThunks.createOne(finalPayload));
    dispatch(auctionRuleTemplatesThunks.list(buildListParams()));
    closeModal();
  }

  const isLoading = status === "loading";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Auction Rule Templates
          </h1>
          <p className="text-sm text-gray-500">{count} registro(s)</p>
        </div>

        <Button className="text-slate-600" onClick={openCreate}>
          Nova Template
        </Button>
      </div>

      {error && (
        <Alert
          color="failure"
          onDismiss={() => dispatch(auctionRuleTemplatesActions.clearError())}
        >
          {error}
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por título..."
        />
        <Button color="gray" onClick={onSearch} disabled={isLoading}>
          Buscar
        </Button>
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando...
            </span>
          </div>
        ) : (
          <Table hoverable>
            <TableHead>
              <TableHeadCell>Title</TableHeadCell>
              <TableHeadCell>Rules (preview)</TableHeadCell>
              <TableHeadCell className="text-right">Ações</TableHeadCell>
            </TableHead>

            <TableBody className="divide-y">
              {items?.length ? (
                items.map((r) => (
                  <TableRow
                    key={r.id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <TableCell className="font-medium text-gray-900 dark:text-white">
                      {r.title}
                    </TableCell>
                    <TableCell className="text-gray-600 dark:text-gray-300">
                      {truncate(r.rules_text, 160)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => openRead(r)}
                        >
                          Ver
                        </Button>
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => openEdit(r)}
                        >
                          Editar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-6 text-gray-500"
                  >
                    Nenhuma template encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <AuctionRuleTemplateFormModal
        open={modalOpen}
        onClose={closeModal}
        initialData={editing}
        saving={isLoading}
        onSubmit={onSubmit}
      />
      <AuctionRuleTemplateReadModal
        open={readOpen}
        onClose={closeRead}
        template={reading}
      />
    </div>
  );
}
